import {
    Check,
    CheckCircle2,
    ChevronRight,
    CreditCard,
    Info,
    ShieldCheck,
    WalletCards,
} from "lucide-react";
import { useMemo } from "react";
import { useAppSelector } from "../../../../../Redux/hooks";
import { RootState } from "../../../../../store";
import "./CheckoutPaymentOptions.css";
import PaymentSuccessOverlay from "../PaymentSuccessOverlay/PaymentSuccessOverlay";

const COMPONENT_TO_SALAR_PAY: Record<string, string> = {
    REFERRAL_COMMISSION: "REFERRAL_COMMISSION",
    FLOW_COMMISSION: "SALAR_FLOW_COMMISSION",
    SAMRIDDHI_COMMISSION: "SALAR_SAMRIDDHI_COMMISSION",
    COMMUNITY_GROWTH_COMMISSION: "SALAR_COMMUNITY_GROWTH_COMMISSION",
    UNITY_COMMISSION: "SALAR_UNITY_COMMISSION",
    PEARLS_COMMISSION: "SALAR_PEARLS_COMMISSION",
    MILESTONE_REFERRAL_COMMISSION: "SALAR_MILESTONE_REFERRAL_COMMISSION",
    PARTNER_GROWTH_REFERRAL: "SALAR_PARTNER_GROWTH_REFERRAL",
    FUNDS: "SALAR_FUNDS",
    SHOPPING_VOUCHER: "SHOPPING_VOUCHER",
};

export type CheckoutPaymentOptionsProps = {
    selectedWallets: string[];
    onToggleWallet: (type: string) => void;
    selectedOnlineMethod: string;
    onSelectOnlineMethod: (method: string) => void;
    grandTotal: number;
    onPlaceOrder: () => void;
    isPlacingOrder: boolean;
    checkoutLoading: boolean;
    payDisabled: boolean;
    undeliverable: boolean;
    pdpDeliveryLoading: boolean;
    hasPhysicalItem: boolean;
    paymentSuccessOpen: boolean;
    onSuccessContinue: () => void;
};

const formatAmount = (value: number) =>
    new Intl.NumberFormat("en-IN", {
        maximumFractionDigits: 2,
    }).format(Math.max(0, value));

const CheckoutPaymentOptions = ({
    selectedWallets,
    onToggleWallet,
    selectedOnlineMethod,
    onSelectOnlineMethod,
    grandTotal,
    onPlaceOrder,
    isPlacingOrder,
    checkoutLoading,
    payDisabled,
    undeliverable,
    pdpDeliveryLoading,
    hasPhysicalItem,
    paymentSuccessOpen,
    onSuccessContinue,
}: CheckoutPaymentOptionsProps) => {
    const { balance } = useAppSelector((state: RootState) => state.wallet);

    // Build available wallet rows dynamically from real Redux balance
    const commissionRows = useMemo(() => {
        if (!balance) return [];
        if (balance.components) {
            return balance.components
                .filter((comp) => comp.isEligible && comp.balance > 0)
                .map((comp) => {
                    const mappedType = COMPONENT_TO_SALAR_PAY[comp.component] || comp.component;
                    return {
                        id: mappedType,
                        name: comp.displayName,
                        balance: comp.balance,
                    };
                });
        }
        // Fallback for older balance structure
        return [
            {
                id: "REFERRAL_COMMISSION",
                name: "Referral Incentives",
                balance: balance.commissionBalances?.referralCommission ?? 0,
            },
            {
                id: "SALAR_FLOW_COMMISSION",
                name: "Salar Flow Incentives",
                balance: balance.commissionBalances?.flowCommission ?? 0,
            },
            {
                id: "SALAR_UNITY_COMMISSION",
                name: "Salar Unity Incentives",
                balance: balance.commissionBalances?.unityCommission ?? 0,
            },
            {
                id: "SALAR_COMMUNITY_GROWTH_COMMISSION",
                name: "Salar Community Growth Incentives",
                balance: balance.commissionBalances?.communityGrowthCommission ?? 0,
            },
            {
                id: "SALAR_SAMRIDDHI_COMMISSION",
                name: "Salar Samriddhi Incentives",
                balance: balance.commissionBalances?.samriddhiCommission ?? 0,
            },
            {
                id: "SALAR_MILESTONE_REFERRAL_COMMISSION",
                name: "Salar Milestone Referral Incentives",
                balance: balance.commissionBalances?.milestoneReferralCommission ?? 0,
            },
            {
                id: "SALAR_PEARLS_COMMISSION",
                name: "Salar Pearls Incentives",
                balance: balance.commissionBalances?.pearlsCommission ?? 0,
            },
            {
                id: "SALAR_PARTNER_GROWTH_REFERRAL",
                name: "Salar Partner Growth Referral Incentives",
                balance: balance.commissionBalances?.partnerGrowthReferral ?? 0,
            },
            {
                id: "SALAR_FUNDS",
                name: "Funds",
                balance: balance.funds ?? 0,
            },
        ].filter((row) => row.balance > 0);
    }, [balance]);

    const balanceMap: Record<string, number> = useMemo(() => {
        return Object.fromEntries(commissionRows.map((r) => [r.id, r.balance]));
    }, [commissionRows]);

    const walletTotal = useMemo(() => {
        return selectedWallets.reduce(
            (sum, type) => sum + Math.min(balanceMap[type] ?? 0, grandTotal),
            0
        );
    }, [selectedWallets, balanceMap, grandTotal]);

    const remaining = Math.max(grandTotal - walletTotal, 0);
    const fullyCovered = walletTotal >= grandTotal && selectedWallets.length > 0;

    const buttonLabel = checkoutLoading || isPlacingOrder
        ? "Processing..."
        : undeliverable
            ? "Delivery unavailable"
            : pdpDeliveryLoading && hasPhysicalItem
                ? "Checking delivery…"
                : "Confirm and Pay";

    return (
        <section className="checkout-payment-section">
            <div className="checkout-payment-header">
                <div>
                    <span>PAYMENT</span>
                    <h2>Payment Options</h2>
                </div>

                <div className="checkout-payment-header-icon">
                    <WalletCards size={18} strokeWidth={1.9} />
                </div>
            </div>

            <div className="checkout-payment-card">
                <div className="checkout-salar-pay-head">
                    <div className="checkout-salar-pay-logo">
                        <img
                            src="/assets/img/salarlogo.png"
                            alt="Salar"
                            className="checkout-salar-pay-logo-image"
                        />
                    </div>

                    <div>
                        <strong>Salar Pay</strong>
                        <small>Apply eligible incentives & wallet balance</small>
                    </div>
                </div>

                <div className="checkout-incentive-list">
                    {commissionRows.length === 0 ? (
                        <p style={{ padding: "12px 16px", color: "#667085", fontSize: "12.5px", margin: 0 }}>
                            No wallet or incentive balance currently available.
                        </p>
                    ) : (
                        commissionRows.map((option) => {
                            const selected = selectedWallets.includes(option.id);

                            return (
                                <label
                                    className={`checkout-incentive-item ${selected ? "selected" : ""}`}
                                    key={option.id}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selected}
                                        onChange={() => onToggleWallet(option.id)}
                                    />

                                    <span className="checkout-incentive-check">
                                        {selected && <Check size={10} strokeWidth={3} />}
                                    </span>

                                    <span className="checkout-incentive-name">
                                        {option.name}
                                    </span>

                                    <strong>
                                        ₹ {formatAmount(option.balance)}
                                    </strong>
                                </label>
                            );
                        })
                    )}
                </div>

                {selectedWallets.length > 0 && (
                    <div className="checkout-incentive-total">
                        <span>Selected incentives total:</span>
                        <strong>₹ {formatAmount(walletTotal)}</strong>
                    </div>
                )}

                {selectedWallets.length > 0 && (
                    <div
                        className={`checkout-coverage-banner ${
                            fullyCovered ? "success" : "warning"
                        }`}
                    >
                        {fullyCovered ? (
                            <>
                                <CheckCircle2 size={15} />
                                <span>Fully covered by Salar Pay — no online payment needed.</span>
                            </>
                        ) : (
                            <>
                                <Info size={15} />
                                <span>
                                    ₹{remaining.toFixed(2)} remaining — online payment will be charged.
                                </span>
                            </>
                        )}
                    </div>
                )}

                <div className="checkout-payment-divider" />

                <div
                    className="checkout-online-payment"
                    style={fullyCovered ? { opacity: 0.45, pointerEvents: "none" } : undefined}
                >
                    <div className="checkout-online-payment-title">
                        <div>
                            <CreditCard size={15} strokeWidth={1.9} />
                            <h3>Online Payment</h3>
                        </div>

                        <span>{fullyCovered ? "Not required" : "Choose one"}</span>
                    </div>

                    {/* Cashfree (Default & Recommended) */}
                    <label
                        className={`checkout-payment-method ${
                            selectedOnlineMethod === "CASHFREE" ? "selected" : ""
                        }`}
                    >
                        <input
                            type="radio"
                            name="checkout-payment-method"
                            value="CASHFREE"
                            checked={selectedOnlineMethod === "CASHFREE"}
                            onChange={() => onSelectOnlineMethod("CASHFREE")}
                        />

                        <div className="checkout-payment-method-logo cashfree">
                            <img
                                src="/assets/img/icons/cashfree.png"
                                alt="Cashfree"
                                className="checkout-payment-method-logo-image"
                            />
                        </div>

                        <div className="checkout-payment-method-copy">
                            <strong>Cashfree Payments</strong>
                            <span>Pay securely via UPI, Cards, NetBanking</span>
                        </div>

                        <span className="checkout-payment-radio">
                            {selectedOnlineMethod === "CASHFREE" && <span />}
                        </span>
                    </label>

                    {/* PhonePe */}
                    {/* <label
                        className={`checkout-payment-method ${
                            selectedOnlineMethod === "PHONEPE" ? "selected" : ""
                        }`}
                    >
                        <input
                            type="radio"
                            name="checkout-payment-method"
                            value="PHONEPE"
                            checked={selectedOnlineMethod === "PHONEPE"}
                            onChange={() => onSelectOnlineMethod("PHONEPE")}
                        />

                        <div className="checkout-payment-method-logo phonepe">
                            <img
                                src="/assets/img/icons/phonepe.png"
                                alt="PhonePe"
                                className="checkout-payment-method-logo-image"
                            />
                        </div>

                        <div className="checkout-payment-method-copy">
                            <strong>PhonePe</strong>
                            <span>Pay securely using PhonePe</span>
                        </div>

                        <span className="checkout-payment-radio">
                            {selectedOnlineMethod === "PHONEPE" && <span />}
                        </span>
                    </label> */}
                </div>
            </div>

            <button
                type="button"
                className="checkout-confirm-pay"
                disabled={payDisabled || isPlacingOrder}
                onClick={onPlaceOrder}
                style={
                    payDisabled || isPlacingOrder
                        ? { opacity: 0.6, cursor: "not-allowed" }
                        : undefined
                }
            >
                <span>{buttonLabel}</span>
                <ChevronRight size={15} strokeWidth={2} />
            </button>

            <div className="checkout-payment-secure">
                <ShieldCheck size={13} strokeWidth={2} />
                <span>Secure checkout powered by Salar</span>
            </div>

            <PaymentSuccessOverlay
                open={paymentSuccessOpen}
                amount={grandTotal}
                paymentMethod={fullyCovered ? "salarpay" : selectedOnlineMethod}
                onContinue={onSuccessContinue}
            />
        </section>
    );
};

export default CheckoutPaymentOptions;
